import { isScrolledIntoView } from 'utils/view';
import debounce from 'lodash.debounce';

interface RibbonAnchorLinksProps {
    el: HTMLDivElement;
}

const GLOBAL_OFFSET = 0;
const DEBOUNCE_VALUE = 100;

const getElementIdFromHref = (item: Element): string => {
    return item.getAttribute('href')?.replace('#', '') ?? '';
};

// Run the functions on document ready
const RibbonAnchorLinks = ({ el }: RibbonAnchorLinksProps): void => {
    const anchorLinkItems = el.querySelectorAll('.ribbon-anchor-links-item');
    const anchorLinkSticky = el.querySelector('.ribbon-anchor-links-sticky');
    const anchorLinkContent = el.querySelector('.ribbon-anchor-links-list-content');
    const anchorLinkMobileButton = el.querySelector('.ribbon-anchor-links-mobile-button');
    const anchorLinksMobileButtonText = el.querySelector('.ribbon-anchor-links-mobile-button-text');

    if (!anchorLinkItems.length || !anchorLinkSticky || !anchorLinkMobileButton || !anchorLinksMobileButtonText || !anchorLinkContent) {
        return;
    }

    // get sections element that has related id on current page
    const sectionElements = Array.from(anchorLinkItems).map(item => {
        const id = getElementIdFromHref(item);
        return document.querySelector(`#${id}`);
    })
        .filter(item => {
            return item;
        }) as HTMLElement[];

    // add click event to the item to scroll to the section
    anchorLinkItems?.forEach((item, i) => {
        item.addEventListener('click', e => {
            e.preventDefault();

            const id = getElementIdFromHref(item);
            const selectedSection = sectionElements.find(section => {
                return section.getAttribute('id') === id;
            });

            if (!selectedSection) {
                return;
            }

            const sectionPos = selectedSection.getBoundingClientRect().top;
            const offset = el.clientHeight + GLOBAL_OFFSET - 1; // -1 so it detected inside the section

            if (sectionPos) {
                const scrollPos = sectionPos + window.scrollY - offset;

                window.scrollTo({
                    top: scrollPos,
                    behavior: 'smooth',
                });

                el.classList.remove('is-open');
                anchorLinkMobileButton.setAttribute('aria-expanded', el.classList.contains('is-open') ? 'true' : 'false');
            }
        });
    });

    anchorLinkMobileButton.addEventListener('click', () => {
        el.classList.toggle('is-open');
        anchorLinkMobileButton.setAttribute('aria-expanded', el.classList.contains('is-open') ? 'true' : 'false');
    });

    const updateActiveItem = (id: string): void => {
        const selectedAnchorLinkItem = Array.from(anchorLinkItems).find(item => {
            const anchorLinkItemId = getElementIdFromHref(item);
            return anchorLinkItemId === id;
        });

        if (!selectedAnchorLinkItem) {
            return;
        }

        anchorLinkItems.forEach(item => {
            item.classList.remove('is-active');
        });

        selectedAnchorLinkItem.classList.add('is-active');
        anchorLinksMobileButtonText.textContent = selectedAnchorLinkItem.textContent;
    };

    const onScroll = (): void => {
        let anySelected = false;

        // if current window.scrollY position is in the section, update active item
        sectionElements.forEach(section => {
            if (isScrolledIntoView(section, el.clientHeight + GLOBAL_OFFSET)) {
                const sectionId = section.getAttribute('id') ?? '';
                if (!sectionId) {
                    return;
                }

                updateActiveItem(sectionId);
                anySelected = true;
            }
        });

        // if any of the sections is in view, return
        if (anySelected) {
            return;
        }

        // if the position is above the 1st section
        if (window.scrollY < sectionElements[0].getBoundingClientRect().top + window.scrollY) {
            const sectionId = sectionElements[0].getAttribute('id') ?? '';
            if (!sectionId) {
                return;
            }
            updateActiveItem(sectionId);
        }
    };

    onScroll();
    window.addEventListener('scroll', onScroll);

    window.addEventListener('resize', debounce(onScroll, DEBOUNCE_VALUE));

    // To fix justify content center combine with overflow-x auto
    // Create a ResizeObserver to monitor the content's width
    const resizeObserver = new ResizeObserver(() => {
        // Check if the content's width exceeds the container's width
        if (anchorLinkSticky.scrollWidth > anchorLinkContent.clientWidth) {
            anchorLinkSticky.classList.add('is-overflowing');
            // Content overflows, adjust alignment
            // (anchorLinkContent as HTMLDivElement).style.justifyContent = 'flex-start';
        } else {
            // Content fits within the container, center it
            anchorLinkSticky.classList.remove('is-overflowing');
            // (anchorLinkContent as HTMLDivElement).style.justifyContent = 'center';
        }
    });

    // Observe the content element for size changes
    resizeObserver.observe(anchorLinkContent);
};

export default RibbonAnchorLinks;
